import * as React from "react";

import { AttachedFile } from "modules/gallery/models";
import DocumentGallery from "./dialogs/documentGallery";
import PrivateFilesModal from "./privateFilesModal";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import CloseIcon from '@mui/icons-material/Close';
import HoverText from "modules/documents/components/action-buttons/hoverText";
import { documentsApi } from "api/instances";
import { Document } from "modules/documents";

import "./fileAttachments.sass"

const DEFAULT_BUTTON_LABEL = "ADD/EDIT FILES"
const DEFAULT_REMOVE_LABEL = "REMOVE ALL FILES"
const DEFAULT_NO_ATTACHMENTS_MSG = "No attachments found"
const DEFAULT_FOOTER_MSG = "Attaching large files can affect publishing time"

interface IFileAttachmentsProps {
  show: boolean;
  fileAttachments: AttachedFile[] | undefined;
  onChange: (fileAttachments: AttachedFile[]) => void;
  onRemove: () => void;
  buttonLabel?: string;
  itemCloseIcon?: JSX.Element;
  removeLabel?: string;
  footNoteStyle?: React.CSSProperties;
  isGreyPaper?: boolean;
  v2?: boolean; //authoring v2 layout
  totalFileAttachmentsMBSizeLimit?: number;
  enablePrivateFileModal?: boolean; // used for optional private file modal flow when attaching files restricted to audiences
}

const FileAttachments: React.FC<IFileAttachmentsProps> = ({
    show,
    fileAttachments = [],
    buttonLabel,
    itemCloseIcon,
    removeLabel,
    footNoteStyle,
    isGreyPaper,
    v2 = false,
    totalFileAttachmentsMBSizeLimit,
    onChange,
    onRemove,
    enablePrivateFileModal = false,
}) => {
    const [showDocumentGallery, setShowDocumentGallery] = React.useState(false);
    const [showPrivateFileModal, setShowPrivateFileModal] = React.useState(false);
    const [fileAttachmentDocumentsProperties, setFileAttachmentDocumentsProperties] = React.useState<Document[]>([]);
    const [isFetchingPrivateFileDocument, setIsFetchingPrivateFileDocument] = React.useState(false);
    const [internalFileAttachments, setInternalFileAttachments] = React.useState<AttachedFile[]>(fileAttachments);

    const getEmptyAttachmentText = () : JSX.Element => {
        if (fileAttachments.length === 0)
            return (
            <div className="no-attachment-message">{DEFAULT_NO_ATTACHMENTS_MSG}</div>);
        
        return <React.Fragment/>;
    }

    const onApplyDocumentGalleryFileAttachments = async (fileAttachments: AttachedFile[]) => {
        if (enablePrivateFileModal) {
            setIsFetchingPrivateFileDocument(true);

            try {
                const documentDetailsReq = (await documentsApi.GetBulkDocumentProperties(fileAttachments.map((file) => file.ospreyId))).data;

                const isAnyDocumentRestricted = documentDetailsReq.some((doc) => doc.audiences.length > 0);

                if (isAnyDocumentRestricted) {
                    setFileAttachmentDocumentsProperties(documentDetailsReq);
                    setInternalFileAttachments(fileAttachments);
                    setShowDocumentGallery(false);
                    setShowPrivateFileModal(true);
                }
                else {
                    onApplyFileAttachmentChanges(fileAttachments);
                }
            }
            finally {
                setIsFetchingPrivateFileDocument(false);
            }
        }
        else {
            onApplyFileAttachmentChanges(fileAttachments);
        }
    }

    const onApplyFileAttachmentChanges = (fileAttachments: AttachedFile[]) => {
        onChange(fileAttachments);
        onHideDocumentGallery(fileAttachments);
    }

    const onHideDocumentGallery = (fileAttachments?: AttachedFile[]) => {
        setShowDocumentGallery(false);
        onHidePrivateFileModal(fileAttachments);
        setIsFetchingPrivateFileDocument(false);
    }

    const onRemoveFileAttachment = (fileAttachment: AttachedFile) => {
        const updatedFileAttachments = fileAttachments!.filter((file) => file.ospreyId !== fileAttachment.ospreyId);
        onChange(updatedFileAttachments);
        setInternalFileAttachments(updatedFileAttachments);
    }

    const onRemoveAllFileAttachments = () => {
        setInternalFileAttachments([]);
        onRemove();
    }

    const onShowDocumentGallery = () => {
        setShowDocumentGallery(true);
    }

    const onDenyPrivateFileModal = () => {
        setShowPrivateFileModal(false);
        setFileAttachmentDocumentsProperties([]);
        setShowDocumentGallery(true);
    }

    const onHidePrivateFileModal = (newFileAttachments?: AttachedFile[]) => {
        setShowPrivateFileModal(false);
        setFileAttachmentDocumentsProperties([]);
        setInternalFileAttachments(newFileAttachments ?? fileAttachments);
    }

    const _getTitle = (): string => {
        let label = "Attach files";
        if (fileAttachments.length === 1)
            label = "Attached file (1)";
        else if (fileAttachments.length > 1)
            label = `Attached files (${fileAttachments.length})`;

        return label;
    }

    const _getItem = (fileAttachment: AttachedFile, idx: number) => 
        <Paper
            key={fileAttachment.ospreyId}
            elevation={1}
            className="file-attachment-item"
            style={{
                height: 40,
                border: "solid 1px rgba(0, 0, 0, 0.23)",
                boxShadow: "none",
                boxSizing: "border-box",
                margin: "5px 0 0 0",
            }}
        >
            <div>
                {/* only show 37 characters */}
                {fileAttachment.fileName.length > 37
                    ? <HoverText popoverProps={{ anchorOrigin: { horizontal: "left", vertical: "center"}}} underline={false} label={`${fileAttachment.fileName.substring(0, 37)}...`}>
                        <span id={`file-attachment-item-name-${idx}`} style={{ whiteSpace: "normal", wordBreak:"break-all" }}>{fileAttachment.fileName}</span>
                        </HoverText>
                    : <span id={`file-attachment-item-name-${idx}`}>{fileAttachment.fileName}</span>}
            </div>
            <div>
                <IconButton 
                    style={{ border: "none", boxShadow: "none", background: "none", margin: "0px" ,paddingRight: "0px" }} 
                    size="small" 
                    onClick={() => onRemoveFileAttachment(fileAttachment)}
                    edge="end"
                >
                    {itemCloseIcon || <CloseIcon style={{ color: "#b72026" }} />}
                </IconButton>
            </div>
        </Paper>;

    return (
        <Collapse className="file-attachment" in={show}>
            <Paper elevation={0} className={`section-card ${isGreyPaper ? "grey-background" : "white-background"}`}>
                <div className="optional-content-header">
                    <div>{_getTitle()}</div>
                    {v2
                        ? <Button 
                        variant="text" 
                        color="primary" 
                        size="small" 
                        startIcon={<AddIcon fontSize="small" />} 
                        onClick={onShowDocumentGallery} 
                        className="add-new-item file-attachment-button"
                        id="add-new-file-attachment-button"
                        >
                            {buttonLabel || DEFAULT_BUTTON_LABEL }
                        </Button>
                        : <>
                            {fileAttachments.length > 0 &&
                                <div className="remove-optional-content">
                                    <Button variant="text" color="inherit" startIcon={<BlockIcon />} onClick={onRemoveAllFileAttachments}>Remove All Attachments</Button>
                                </div>}
                            </>}
                </div>
                <div className="file-content-container">
                    {
                        fileAttachments.length > 0 && fileAttachments.map(_getItem)
                    }
                </div>
                {v2
                    ? fileAttachments.length > 1 &&
                        <div className="remove-optional-content">
                            <Button variant="text" color="inherit" className="file-attachment-button" startIcon={<BlockIcon />} onClick={onRemoveAllFileAttachments}>{removeLabel || DEFAULT_REMOVE_LABEL}</Button>
                        </div>
                    :
                    <Button variant="text" color="primary" size="small" startIcon={<AddIcon fontSize="small" />} onClick={onShowDocumentGallery} className="add-new-item file-attachment-button">
                            {buttonLabel || DEFAULT_BUTTON_LABEL}
                    </Button>}

                {getEmptyAttachmentText()}

                <div className="optional-content-disclaimer" style={{ ...footNoteStyle }}>
                    {totalFileAttachmentsMBSizeLimit ? `Total attachments must be ${totalFileAttachmentsMBSizeLimit}MB or less.` : DEFAULT_FOOTER_MSG}
                </div>
                <DocumentGallery
                    show={showDocumentGallery}
                    fileAttachments={internalFileAttachments}
                    onApply={onApplyDocumentGalleryFileAttachments}
                    onClose={() => onHideDocumentGallery()}
                    totalFileAttachmentsMBSizeLimit={totalFileAttachmentsMBSizeLimit}
                    isLoadingApplyButton={isFetchingPrivateFileDocument}
                />
                <PrivateFilesModal
                    enablePrivateFileModal={!!enablePrivateFileModal}
                    showPrivateFileModal={showPrivateFileModal}
                    onClose={() => onHideDocumentGallery()}
                    onDeny={onDenyPrivateFileModal}
                    onConfirm={() => onApplyFileAttachmentChanges(internalFileAttachments)}
                    fileAttachmentDocumentsProperties={fileAttachmentDocumentsProperties}
                />
            </Paper>
        </Collapse>
    );
}

export default FileAttachments;
